import { deriveEmailIds } from '@/lib/automations/derive-email-ids'
import type { AutomationState } from '@/lib/data/convex/automations-transforms'
import type { SubAgentTab } from './types'

export type MentionAutomationForExpansion = Pick<
  AutomationState,
  'automationId' | 'nodes'
>

export const ARTIFACT_MENTION_DRAG_MIME = 'application/x-brew-artifact-mention'

/**
 * Detects an inline artifact mention — `email "Title" (emailId: …)` /
 * `automation "Title" (automationId: …)` — in message prose. Single
 * source for both the user and assistant text renderers.
 */
export const ARTIFACT_MENTION_RE =
  /(?:email|automation)\s+"(?:\\"|[^"])+"\s+\((?:emailId|automationId):\s*[^)]+\)/

export type ArtifactMentionPayload = {
  entityId: string
  kind: 'email' | 'automation'
  title: string
  searchValue: string
}

export type ArtifactMentionSegment =
  | { type: 'text'; text: string }
  | { type: 'mention'; mention: ArtifactMentionPayload }

const ARTIFACT_MENTION_PATTERN =
  /(email|automation)\s+"((?:\\"|[^"])*)"\s+\((emailId|automationId):\s*([^)]+)\)/g

export function buildEntityMentionReference(args: {
  kind: 'email' | 'automation'
  id: string
  title: string
}) {
  const { kind, id, title } = args
  const escapedTitle = title.replace(/"/g, '\\"')

  return kind === 'email'
    ? `email "${escapedTitle}" (emailId: ${id})`
    : `automation "${escapedTitle}" (automationId: ${id})`
}

export function buildArtifactMentionPayload(
  tab: Pick<SubAgentTab, 'emailId' | 'automationId' | 'title' | 'label'>
): ArtifactMentionPayload | null {
  const title = tab.title || tab.label

  if (tab.emailId) {
    return {
      entityId: tab.emailId,
      kind: 'email',
      title,
      searchValue: buildEntityMentionReference({
        kind: 'email',
        id: tab.emailId,
        title,
      }),
    }
  }

  if (tab.automationId) {
    return {
      entityId: tab.automationId,
      kind: 'automation',
      title,
      searchValue: buildEntityMentionReference({
        kind: 'automation',
        id: tab.automationId,
        title,
      }),
    }
  }

  return null
}

export function parseArtifactMentionPayload(
  value: string | null | undefined
): ArtifactMentionPayload | null {
  if (!value) {
    return null
  }

  try {
    const parsed = JSON.parse(value) as Partial<ArtifactMentionPayload>
    if (
      (parsed.kind === 'email' || parsed.kind === 'automation') &&
      typeof parsed.entityId === 'string' &&
      typeof parsed.title === 'string' &&
      typeof parsed.searchValue === 'string'
    ) {
      return {
        entityId: parsed.entityId,
        kind: parsed.kind,
        title: parsed.title,
        searchValue: parsed.searchValue,
      }
    }
  } catch {
    return null
  }

  return null
}

export function parseArtifactMentionSegments(
  text: string
): Array<ArtifactMentionSegment> {
  const segments: Array<ArtifactMentionSegment> = []
  let lastIndex = 0

  for (const match of text.matchAll(ARTIFACT_MENTION_PATTERN)) {
    const matchText = match[0]
    const kind = match[1] as 'email' | 'automation'
    const rawTitle = match[2] ?? ''
    const rawId = match[4]?.trim()
    const start = match.index ?? -1

    if (start < 0 || !rawId) {
      continue
    }

    if (start > lastIndex) {
      segments.push({ type: 'text', text: text.slice(lastIndex, start) })
    }

    segments.push({
      type: 'mention',
      mention: {
        entityId: rawId,
        kind,
        title: rawTitle.replace(/\\"/g, '"'),
        searchValue: matchText,
      },
    })

    lastIndex = start + matchText.length
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', text: text.slice(lastIndex) })
  }

  return segments.length > 0 ? segments : [{ type: 'text', text }]
}

export function extractArtifactMentionIds(text: string): {
  emailIds: Array<string>
  automationIds: Array<string>
} {
  const emailIds = new Set<string>()
  const automationIds = new Set<string>()

  for (const segment of parseArtifactMentionSegments(text)) {
    if (segment.type !== 'mention') {
      continue
    }
    if (segment.mention.kind === 'email') {
      emailIds.add(segment.mention.entityId)
    } else {
      automationIds.add(segment.mention.entityId)
    }
  }

  return {
    emailIds: [...emailIds],
    automationIds: [...automationIds],
  }
}

export function getExpandedComposerEmailIds(
  text: string,
  mentionAutomations?: Array<MentionAutomationForExpansion> | null
): Array<string> {
  const { emailIds, automationIds } = extractArtifactMentionIds(text)
  const matching = (mentionAutomations ?? []).filter((a) =>
    automationIds.includes(a.automationId)
  )
  return expandReferencedEmailIdsWithAutomations({
    emailIds,
    automations: matching,
  })
}

export function expandReferencedEmailIdsWithAutomations(args: {
  emailIds: Array<string>
  automations: Array<Pick<AutomationState, 'nodes'>>
}): Array<string> {
  const expanded = new Set(args.emailIds)

  for (const automation of args.automations) {
    for (const emailId of deriveEmailIds(automation.nodes)) {
      expanded.add(emailId)
    }
  }

  return [...expanded]
}
